import {
  queryOptions,
  useMutation,
  usePrefetchQuery,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useDeferredValue } from 'react';
import { roleQueryKeys, Roles } from '#api';
import type { Successful } from '#dn-types/api.ts';
import type { Role } from '#dn-types/role.ts';
import { makeMaxResourceQueryFn } from '#util/api.ts';

export function useAddRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: Roles.addRole,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: roleQueryKeys.lists() });
    },
  });
}

export const makeRoleQueryOptions = (roleId: string) =>
  queryOptions({
    queryKey: roleQueryKeys.detail(roleId),
    queryFn: ({ signal }) => Roles.getRole(roleId, { signal }),
  });

/**
 * {@link useSuspenseRoleQuery} fetches role details from the api.
 */
export function useSuspenseRoleQuery(roleId: string) {
  return useSuspenseQuery(makeRoleQueryOptions(roleId));
}

/**
 * {@link useConditionalRoleQuery} can be triggered conditionally, for instance on hosts which may or may not have a roleID.
 */
export function useConditionalRoleQuery(
  roleId: string,
  { enabled }: { enabled?: boolean }
): UseQueryResult<Successful<Role>> {
  return useQuery({
    ...makeRoleQueryOptions(roleId),
    enabled,
    throwOnError: true,
  });
}

// TODO: create `makeRolesQueryOptions` and use it around the app for improved query type-safety
export function useSuspenseRolesQuery(cursor?: string, pageSize?: number) {
  const deferredCursor = useDeferredValue(cursor);
  const deferredPageSize = useDeferredValue(pageSize);

  const rolesQuery = useSuspenseQuery({
    queryKey: roleQueryKeys.list(deferredCursor, deferredPageSize),
    queryFn: ({ signal }) => Roles.getRoles({ cursor: deferredCursor, pageSize: deferredPageSize, init: { signal } }),
  });

  return {
    data: rolesQuery.data,
    isFetching: deferredCursor !== cursor || deferredPageSize !== pageSize || rolesQuery.isFetching,
  };
}

export const maxRolesQueryOptions = queryOptions({
  queryKey: roleQueryKeys.maxList,
  queryFn: makeMaxResourceQueryFn(Roles.getRoles),
});

export function useGetMaxRolesQuery() {
  return useQuery(maxRolesQueryOptions);
}

export function useSuspenseGetMaxRolesQuery() {
  return useSuspenseQuery(maxRolesQueryOptions);
}

export function usePrefetchRoles() {
  usePrefetchQuery(maxRolesQueryOptions);
}
